/** Egypt / Cairo timezone for exam schedule windows. */
export const CAIRO_TZ = "Africa/Cairo";

/**
 * Normalize API datetime to "YYYY-MM-DD HH:mm:ss" (space-separated).
 * @returns {string|null}
 */
export function normalizeApiDateTime(value) {
  if (value == null || String(value).trim() === "") return null;
  let s = String(value).trim().replace("T", " ");
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) {
    s = `${s}:00`;
  }
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) return null;
  return s.slice(0, 19);
}

/**
 * Current wall-clock time in Africa/Cairo as "YYYY-MM-DD HH:mm:ss".
 */
export function getCairoNowString(now = Date.now()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: CAIRO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(now));

  const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  let hour = get("hour");
  if (hour === "24") hour = "00";

  return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
}

/**
 * Parse a Cairo wall-clock API datetime to a UTC epoch ms.
 * @returns {number} 0 if invalid
 */
export function parseCairoWallTimeToMs(apiDateTime) {
  const local = normalizeApiDateTime(apiDateTime);
  if (!local) return 0;
  const isoLocal = local.replace(" ", "T");
  let utc = Date.parse(`${isoLocal}Z`);
  if (!Number.isFinite(utc)) return 0;

  const dtf = new Intl.DateTimeFormat("en-GB", {
    timeZone: CAIRO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const asCairoString = (ms) => {
    const parts = dtf.formatToParts(new Date(ms));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    let hour = get("hour");
    if (hour === "24") hour = "00";
    return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
  };

  for (let i = 0; i < 3; i += 1) {
    const shown = asCairoString(utc);
    const shownAsUtc = Date.parse(`${shown.replace(" ", "T")}Z`);
    const desiredAsUtc = Date.parse(`${isoLocal}Z`);
    if (!Number.isFinite(shownAsUtc) || !Number.isFinite(desiredAsUtc)) break;
    utc += desiredAsUtc - shownAsUtc;
  }

  return utc;
}

function hasExplicitTimezone(value) {
  const s = String(value).trim();
  if (/[zZ]$/.test(s)) return true;
  if (/[+-]\d{2}:\d{2}$/.test(s)) return true;
  if (/[+-]\d{4}$/.test(s)) return true;
  return false;
}

/**
 * Parse an API datetime to UTC epoch ms.
 * Naive Laravel values ("Y-m-d H:i:s") are Egypt / Cairo wall-clock.
 * Strings with Z or an offset are treated as absolute instants.
 * @returns {number} 0 if invalid
 */
export function parseApiDateTimeToMs(value) {
  if (value == null || String(value).trim() === "") return 0;
  const raw = String(value).trim();
  if (hasExplicitTimezone(raw)) {
    const t = Date.parse(raw.includes("T") ? raw : raw.replace(" ", "T"));
    return Number.isFinite(t) ? t : 0;
  }
  return parseCairoWallTimeToMs(raw);
}

function earliestCreatedAt(values) {
  const valid = values.filter((value) => parseApiDateTimeToMs(value));
  if (!valid.length) return null;
  valid.sort((a, b) => parseApiDateTimeToMs(a) - parseApiDateTimeToMs(b));
  return valid[0];
}

/**
 * created_at from GET /students/student_exams/get_student_exam/{exam_id}.
 * A list uses the earliest row, so a later re-entry cannot move the start forward.
 * @returns {string|null}
 */
export function readStudentExamCreatedAt(body) {
  if (!body || typeof body !== "object") return null;

  const attemptNodes = [body.student_exam, body.data?.student_exam].filter(Boolean);
  [body, body.data, body.data?.data].forEach((node) => {
    if (Array.isArray(node)) attemptNodes.push(...node);
  });
  const fromAttempt = earliestCreatedAt(attemptNodes.map((node) => node?.created_at));
  if (fromAttempt) return fromAttempt;

  return earliestCreatedAt([
    body.created_at,
    body.data?.created_at,
    body.data?.data?.created_at,
  ]);
}

/**
 * Absolute instant when a timed attempt ends.
 * Naive `createdAt` values are Africa/Cairo wall-clock (the API server).
 * Values with Z or an offset are absolute. Compared with the device clock,
 * so the remaining duration is the same in the student's local timezone.
 * Never longer than the full duration if the device clock is behind the server.
 * @returns {number|null} epoch ms, including a past timestamp when time is already up
 */
export function examAttemptEndsAtMs(createdAt, durationMinutes, now = Date.now()) {
  const mins = Number(durationMinutes);
  if (!Number.isFinite(mins) || mins <= 0) return null;
  const startedAt = parseApiDateTimeToMs(createdAt);
  if (!startedAt) return null;
  const durationMs = mins * 60 * 1000;
  return Math.min(startedAt + durationMs, now + durationMs);
}

/** Extra time to upload a PDF after a quiz (type 0) or exam (type 1) timer ends. */
export const PDF_SUBMIT_GRACE_MS = 10 * 60 * 1000;

/**
 * Submission window after the attempt timer hits zero.
 * The grace period is measured from the attempt end, not from when the dialog opens.
 */
export function pdfSubmitWindow(attemptEndsAt, now = Date.now()) {
  if (attemptEndsAt == null || !Number.isFinite(attemptEndsAt)) {
    return { examTimeUp: false, windowClosed: false, graceEndsAt: null };
  }
  const graceEndsAt = attemptEndsAt + PDF_SUBMIT_GRACE_MS;
  return {
    examTimeUp: now >= attemptEndsAt,
    windowClosed: now >= graceEndsAt,
    graceEndsAt,
  };
}

/**
 * Format an API datetime in the student's local timezone.
 * @returns {string|null}
 */
export function formatApiDateTimeLocal(value, { timeOnly = false } = {}) {
  const ms = parseApiDateTimeToMs(value);
  if (!ms) return null;
  const opts = timeOnly
    ? { hour: "numeric", minute: "2-digit" }
    : {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      };
  return new Intl.DateTimeFormat(undefined, opts).format(new Date(ms));
}

/** e.g. "17 Sep 2026, 9:00 PM – 10:00 PM" in the student's local timezone. */
export function formatApiDateTimeRangeLocal(fromVal, toVal) {
  const fromMs = parseApiDateTimeToMs(fromVal);
  const toMs = parseApiDateTimeToMs(toVal);
  if (!fromMs) return "—";
  const fromLabel = formatApiDateTimeLocal(fromVal);
  if (!fromLabel) return "—";
  if (!toMs) return fromLabel;
  const sameDay =
    new Date(fromMs).toDateString() === new Date(toMs).toDateString();
  const toLabel = formatApiDateTimeLocal(toVal, { timeOnly: sameDay });
  return toLabel ? `${fromLabel} – ${toLabel}` : fromLabel;
}

/** Seconds until an API datetime; negative if already passed. Null if invalid. */
export function secondsUntilApiDateTime(value, now = Date.now()) {
  const ms = parseApiDateTimeToMs(value);
  if (!ms) return null;
  return Math.floor((ms - now) / 1000);
}

function formatDurationParts(msRemaining) {
  let seconds = Math.floor(msRemaining / 1000);
  if (seconds <= 0) return null;

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (days === 0 && hours === 0) {
    if (minutes > 0) parts.push(`${minutes}m`);
    if (minutes < 5 || parts.length === 0) parts.push(`${Math.max(secs, 0)}s`);
  } else if (minutes > 0 || parts.length === 0) {
    parts.push(`${Math.max(minutes, 1)}m`);
  }

  return parts.slice(0, 2).join(" ");
}

/**
 * True when Cairo now is at or after the given API datetime.
 * Empty / missing datetime → false (no deadline).
 */
export function isPastCairoDateTime(apiDateTime, now = Date.now()) {
  const end = normalizeApiDateTime(apiDateTime);
  if (!end) return false;
  return getCairoNowString(now) >= end;
}

/**
 * True when Cairo now is still before the given API datetime.
 * Empty / missing datetime → false (already available).
 */
export function isBeforeCairoDateTime(apiDateTime, now = Date.now()) {
  const start = normalizeApiDateTime(apiDateTime);
  if (!start) return false;
  return getCairoNowString(now) < start;
}

/** Exam / classified assignment has an end_time that has passed (Cairo). */
export function isExamPastEndTime(examOrEndTime, now = Date.now()) {
  const endTime =
    examOrEndTime && typeof examOrEndTime === "object"
      ? examOrEndTime.end_time
      : examOrEndTime;
  return isPastCairoDateTime(endTime, now);
}

function formatClosesInFromMs(targetMs, now) {
  if (!targetMs || targetMs <= now) return null;
  const duration = formatDurationParts(targetMs - now);
  if (!duration) return null;
  return `closes in ${duration}`;
}

/**
 * Human label for time until an API datetime.
 * @returns {string|null} e.g. "closes in 2d 5h"
 */
export function formatExamClosesIn(endTime, now = Date.now()) {
  return formatClosesInFromMs(parseApiDateTimeToMs(endTime), now);
}

/** Late submissions stay open this long after the group due date or end_time. */
export const LATE_SUBMISSION_MS = 24 * 60 * 60 * 1000;

function isSetDateTime(value) {
  return value != null && String(value).trim() !== "";
}

/**
 * Deadline for this student: matching group pivot.due_date, otherwise exam.end_time.
 * @returns {string|null}
 */
export function resolveStudentExamDeadline(exam, studentGroupId) {
  if (!exam || typeof exam !== "object") return null;

  const groupId =
    studentGroupId != null && String(studentGroupId).trim() !== ""
      ? String(studentGroupId)
      : null;

  if (groupId && Array.isArray(exam.groups)) {
    const match = exam.groups.find((group) => {
      const pivotId = group?.pivot?.student_group_id;
      if (pivotId != null && String(pivotId) === groupId) return true;
      return group?.id != null && String(group.id) === groupId;
    });
    const due = match?.pivot?.due_date;
    if (isSetDateTime(due) && parseApiDateTimeToMs(due)) return due;
  }

  if (isSetDateTime(exam.end_time) && parseApiDateTimeToMs(exam.end_time)) {
    return exam.end_time;
  }
  return null;
}

/**
 * Open / late / closed state for a student exam, quiz, or homework.
 * Naive API datetimes are Cairo wall-clock; labels use the student's local timezone.
 * No deadline → no countdown and the item stays open.
 */
export function getStudentExamDeadlineState(exam, studentGroupId, now = Date.now()) {
  const deadline = resolveStudentExamDeadline(exam, studentGroupId);
  const deadlineMs = parseApiDateTimeToMs(deadline);
  if (!deadline || !deadlineMs) {
    return {
      hasDeadline: false,
      deadline: null,
      dueLabel: null,
      pastDue: false,
      closed: false,
      late: false,
      closesIn: null,
    };
  }

  const hardCloseMs = deadlineMs + LATE_SUBMISSION_MS;
  const pastDue = now >= deadlineMs;
  const closed = now >= hardCloseMs;
  const late = pastDue && !closed;
  const closesIn = closed
    ? null
    : formatClosesInFromMs(late ? hardCloseMs : deadlineMs, now);

  return {
    hasDeadline: true,
    deadline,
    dueLabel: formatApiDateTimeLocal(deadline),
    pastDue,
    closed,
    late,
    closesIn,
  };
}

/** Subtitle while the item is still open. Null when there is no deadline or it is closed. */
export function formatStudentExamDeadlineLine(state) {
  if (!state?.hasDeadline || state.closed) return null;
  if (state.late) {
    return state.closesIn ? `Late submission · ${state.closesIn}` : "Late submission";
  }
  const dueBit = state.dueLabel ? `Due ${state.dueLabel}` : null;
  const line = [dueBit, state.closesIn].filter(Boolean).join(" · ");
  return line || null;
}
